import { Input, InputNumber, DatePicker } from 'antd'
import { Moment } from 'moment'
import { CIMSPage } from 'shared'
import LabTechSelect from 'components/CIMSSelect/common/LabTechSelect'
import { LabeledValueType } from 'components/CIMSSelect/types'
import { TabularFormItemColumn } from 'components/TabularFormItems/types'
import { HelpMessage } from 'utils/constants'
import { BreakDataFormItemsValue } from '../../types'
import TestingLabSelect from 'components/CIMSSelect/common/TestingLabSelect'
import JumpingSelectWorkaround from './components/JumpingSelectWorkaround'

export function useBreakDataFormItemColumns<T extends BreakDataFormItemsValue>({
  onChange,
  type,
}: UseBreakDataFormItemColumnOptions<T>): TabularFormItemColumn<T>[] {
  const breakData = HelpMessage.Concrete.ConcreteTestSetTracker.BreakData
  const help = type === 'core' ? breakData.Core : breakData.Cylinder
  return [
    {
      title: 'Testing Lab',
      key: 'testingLab',
      minWidth: 150,
      link: CIMSPage.ConcreteTestingLabs,
      help: help.TestingLab,
      render: (record) => (
        <JumpingSelectWorkaround>
          <TestingLabSelect
            value={record.testingLab}
            onChange={(value) =>
              onChange.testingLab?.(record, value as LabeledValueType)
            }
          />
        </JumpingSelectWorkaround>
      ),
    },
    {
      title: 'Lab Tech',
      key: 'labTech',
      help: help.LabTech,
      link: CIMSPage.ConcreteTestingLabs,
      minWidth: 175,
      render: (record) => (
        <JumpingSelectWorkaround>
          <LabTechSelect
            value={record.labTech}
            onChange={(value) =>
              onChange.labTech?.(record, value as LabeledValueType)
            }
          />
        </JumpingSelectWorkaround>
      ),
    },
    {
      key: 'labNumber',
      title: 'Lab Number',
      help: help.LabNumber,
      render: (record) => (
        <Input
          allowClear
          value={record.labNumber ?? undefined}
          onChange={({ target: { value } }) =>
            onChange.labNumber?.(record, value)
          }
        />
      ),
    },
    {
      key: 'age',
      title: 'Age',
      help: help.Age,
      maxWidth: 65,
      render: (record) => (
        <InputNumber
          value={record.age}
          onChange={(value) => onChange.age?.(record, value as number)}
        />
      ),
    },
    {
      key: 'breakDate',
      title: 'Break Date',
      help: help.BreakDate,
      maxWidth: 125,
      render: (record) => (
        <DatePicker
          allowClear
          value={record.breakDate}
          onChange={(value) => onChange.breakDate?.(record, value as Moment)}
        />
      ),
    },
    {
      key: 'diameter',
      title: 'Diameter',
      help: help.Diameter,
      render: (record) => (
        <Input
          allowClear
          value={record.diameter ?? undefined}
          onChange={({ target: { value } }) =>
            onChange.diameter?.(record, value)
          }
        />
      ),
    },
    {
      key: 'peakLoad',
      title: 'Peak Load',
      help: help.PeakLoad,
      maxWidth: 100,
      render: (record) => (
        <InputNumber
          value={record.peakLoad}
          onChange={(value) => onChange.peakLoad?.(record, value as number)}
        />
      ),
    },
    {
      key: 'strength',
      title: 'Strength',
      help: help.Strength,
      maxWidth: 90,
      render: (record) => (
        <InputNumber
          value={record.strength}
          onChange={(value) => onChange.strength?.(record, value as number)}
        />
      ),
    },
    {
      key: 'type',
      title: 'Type',
      help: help.Type,
      maxWidth: 75,
      render: (record) => (
        <Input
          allowClear
          value={record.type ?? undefined}
          onChange={({ target: { value } }) => onChange.type?.(record, value)}
        />
      ),
    },
  ]
}

type UseBreakDataFormItemColumnOptions<T> = {
  /**
   * helps determine which help messages to use
   */
  type: 'core' | 'test'
  /**
   * onChange callbacks for each input
   */
  onChange: Partial<
    Record<
      keyof BreakDataFormItemsValue,
      (
        record: T,
        value: BreakDataFormItemsValue[keyof BreakDataFormItemsValue]
      ) => void
    >
  >
}
